import { patchTikTokAudio } from './TikTok.api';
import { getTiktokAudios, getTiktokUsers, postTikTokAudio, postTiktokUser, TiktokAudio, TiktokUser } from '@round/api';

export async function updateTikTokAudioAlternateName(musicId: number, alternateName: string) {
    try {
        return await patchTikTokAudio(musicId, { alternate_name: alternateName });
    } catch (e) {
        if (e instanceof Error && e.name === 'AbortError') {
            throw e;
        }

        throw new Error('Could not set custom name');
    }
}

type FindOrCreateReturn<TData> = { success: true; data: TData } | { success: false; error: string };

export async function findOrCreateTiktokAudio(identifier: string): Promise<FindOrCreateReturn<TiktokAudio>> {
    const findResponse = await getTiktokAudios({ identifier_search: identifier });
    if (findResponse.status !== 200) {
        return { success: false, error: 'Error occurred on audio search' };
    }

    let audio: TiktokAudio | null = findResponse.data.results[0] ?? null;
    if (audio) {
        return { success: true, data: audio };
    }

    const postResponse = await postTikTokAudio({ identifier });
    if (postResponse.status === 201) {
        return { success: true, data: postResponse.data };
    }

    const message = Array.isArray(postResponse.data.identifier)
        ? postResponse.data.identifier.toString()
        : postResponse.data.identifier ?? 'Could not create audio';

    return { success: false, error: message };
}

export async function findOrCreateTiktokUser(identifier: string): Promise<FindOrCreateReturn<TiktokUser>> {
    const findResponse = await getTiktokUsers({ identifier_search: identifier });
    if (findResponse.status !== 200) {
        return { success: false, error: findResponse.data.detail || 'Error occurred on user search' };
    }

    let user: TiktokUser | null = findResponse.data.results[0] ?? null;
    if (user) {
        return { success: true, data: user };
    }

    const postResponse = await postTiktokUser({ identifier });
    if (postResponse.status === 201) {
        return { success: true, data: postResponse.data };
    }

    if (postResponse.status === 403) {
        return { success: false, error: 'This request is forbidden' };
    }

    const message = postResponse.data.identifier ? postResponse.data.identifier.toString() : 'Could not create user';

    return { success: false, error: message };
}
